/**
 * Paid Posting Bot — betaalde posts, los van de gratis bot.
 *
 * Bewust een eigen pagina en niet een tabblad op Posting Bot. De twee botten
 * delen wel hun techniek maar niet hun risico: een gratis post verdwijnt na
 * een dag, een betaalde blijft altijd staan. Alles wat hier misgaat blijft
 * dus staan, en dat rechtvaardigt een eigen scherm met eigen prijzen, een
 * eigen contentbron en een eigen wachtrij.
 *
 * De twee platformen rekenen verschillend af, en dat verschil is zichtbaar
 * gehouden in plaats van weggepoetst:
 *
 * * FanCentro kent twee vormen achter "Paid Subscribers" — alleen voor
 *   abonnees, of los te ontgrendelen tegen een prijs in CREDITS (100 = $1;
 *   11-10-2026 Jordy; minimum 500 is een placeholder tot hij het bevestigt);
 * * F2F vraagt eerst wie er moet betalen (vier soorten) en pas daarna
 *   hoeveel, in EURO'S, en bij "Paid for everyone" zijn dat twee bedragen.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  ErrorNote, Loading, Panel, Plat, Seg, Table, Tag,
} from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { api, mediaToken } from '../lib/api'
import { Modal } from '../components/Modal'
import { Weekplanner, type PlanPost } from '../components/Weekplanner'
import { dt, eur, num } from '../lib/format'

/** Ruwe indicatie voor de euro-weergave naast een dollarprijs. */
const USD_NAAR_EUR = 0.92

/** Wat FanCentro minimaal accepteert als ontgrendelprijs. */
const FC_MIN_CREDITS = 500

const FC_MODI = [
  { value: 'subscribers_only', label: 'Subscribers only' },
  { value: 'unlock_price', label: 'Unlockable (credits)' },
]

const F2F_TYPES = [
  { value: 'exclusive_fans', label: 'Exclusive for fans' },
  { value: 'non_fans_pay', label: 'Only non-fans must pay' },
  { value: 'paid_everyone', label: 'Paid for everyone' },
  { value: 'vip', label: 'VIP post' },
]

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]

/**
 * De drie soorten betaald item.
 *
 * Een clip is geen post: hij gaat over een ander domein, is altijd betaald en
 * moet na indienen eerst door een beoordeling. Dat verschil hoort zichtbaar
 * te zijn en niet weggepoetst.
 */
const SOORTEN: Record<string, string> = {
  fc_post: 'FC post',
  f2f_post: 'F2F post',
  fc_clip: 'FC clip',
}

/** Waar een ingediende clip staat in de beoordeling. */
const REVIEW: Record<string, { label: string; kind: 'ok' | 'warn' | 'bad' }> = {
  in_review: { label: 'In review', kind: 'warn' },
  approved: { label: 'Approved', kind: 'ok' },
  rejected: { label: 'Rejected', kind: 'bad' },
}

interface PaidConfig {
  id: number
  model_persona_id: number
  model_name: string
  platform: string
  active: boolean
  interval_minutes: number
  auto_stop_at: string | null
  paid_source: string | null
  /** Een mapnaam die op dit model lijkt maar niet zeker is; alleen gevuld als
   *  er GEEN map gevonden is. Koppelen gebeurt nooit vanzelf. */
  nas_suggestion?: string | null
  nas_options?: string[]
  fc_mode: string | null
  fc_unlock_credits: number | null
  f2f_revenue_type: string | null
  f2f_price_followers_eur: number | null
  f2f_price_fans_eur: number | null
  queued_count: number
  posted_count: number
  needs_review_count: number
  /** Wat F2F vandaag nog toestaat; -1 als het niet gelezen kon worden. */
  f2f_daily_remaining: number | null
  /** 14-10 (fase 2): het wisselende dagschema. Leeg = standaard 1–3. */
  min_per_dag: number | null
  max_per_dag: number | null
  /** running = plan loopt, done = alles ingepland en voorbij (klaar), stopped = uit. */
  plan_status: 'running' | 'done' | 'stopped'
  gepland_tot: string | null
}

/** Wat /dry-run teruggeeft (fase 4A): alleen berekend, niets gedaan. */
interface DryRun {
  hoofdschakelaar: { aan: boolean; door: string | null; op: string | null }
  noodrem: boolean
  horizon_days: number
  note: string
  models: {
    model_persona_id: number; model: string | null; running: boolean
    posts: { post_id: number; platform: string; scheduled_at: string; file: string; would: string }[]
  }[]
  f2f_delete_before_post: {
    model_persona_id: number; day: string; paid_posts: number[]
    would_delete: { post_id: number; platform_post_id: string; scheduled_at: string }[]
    short_by: number
  }[]
}

/** Standaard van het dagschema zolang er niets is ingesteld (gelijk aan de server). */
const SCHEMA_STANDAARD = { min: 1, max: 3 }

/** "999 credits · $9.99 · ≈ €9,19": het bedrag in credits, met dollars en euro ter info. */
function credits(waarde: number | null | undefined): string {
  if (waarde == null) return '—'
  const usd = Number(waarde) / 100
  return `${num(waarde)} credits · $${usd.toFixed(2)} · ≈ ${eur(usd * USD_NAAR_EUR)}`
}

/** Wat de betaalde vervangroute teruggeeft. */
type PaidVervanging = {
  csv_regels: number; gekoppeld: number; csv_zonder_post: number
  posts_zonder_csv: number; al_op_platform: number
  nu_aan_het_publiceren: number; oude_tags_behouden: number
  changed?: number; backup?: string
}

export default function PaidPosting() {
  const [platform, setPlatform] = useState('all')
  const [manageFor, setManageFor] = useState<PaidConfig | null>(null)
  const [kiesMap, setKiesMap] = useState<PaidConfig | null>(null)
  // De planner staat op een eigen tabblad: het overzicht gaat over de bot,
  // de planner over wat er wanneer uitgaat.
  const [tab, setTab] = useState<'models' | 'planner'>('models')
  // De route geeft {items, total} terug, geen kale lijst.
  const planning = useApi<{ items: PlanPost[]; total: number }>(
    '/api/admin/paid-posting/items')
  const { run, busy, notify } = useActions()

  const configs = useApi<PaidConfig[]>('/api/admin/paid-posting/configs')
  // 15-10 (fase 4A): de hoofdschakelaar. Standaard uit; alleen Jordy zet hem om.
  const schakelaar = useApi<{ aan: boolean; door: string | null; op: string | null }>(
    '/api/admin/paid-posting/master-switch')
  const [droog, setDroog] = useState<DryRun | null>(null)
  const draaiDroog = async () => {
    try {
      setDroog(await api.get<DryRun>('/api/admin/paid-posting/dry-run'))
    } catch (fout) {
      notifyFout(fout)
    }
  }
  const notifyFout = (fout: unknown) =>
    notify(fout instanceof Error ? fout.message : String(fout), false)

  /** De map met de hand aan dit model hangen. Eén expliciete keuze, opgeslagen
   *  in `persona_config.paid_posting.nas_folder`. */
  const koppel = (c: PaidConfig, map: string) => run({
    label: 'Link folder',
    confirm: `Link the paid folder "${map}" to ${c.model_name}?`,
    request: mut.put('/api/admin/paid-posting/defaults',
                     { model_persona_id: c.model_persona_id, nas_folder: map }),
    onDone: () => { setKiesMap(null); configs.refetch() },
    successText: `${c.model_name} linked to ${map}.`,
  })

  const rows = useMemo(() => {
    const alles = configs.data ?? []
    return platform === 'all'
      ? alles
      : alles.filter((c) => String(c.platform).toLowerCase() === platform)
  }, [configs.data, platform])

  const actief = rows.filter((c) => c.plan_status === 'running').length

  return (
    <>
      <div className="filters">
        <div className="seg">
          <button type="button" className={tab === 'models' ? 'on' : undefined}
                  onClick={() => setTab('models')}>Models</button>
          <button type="button" className={tab === 'planner' ? 'on' : undefined}
                  onClick={() => { setTab('planner'); planning.refetch() }}>
            Week planner
          </button>
        </div>
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <div className="spacer" style={{ flex: 1 }} />
        <button className="btn ghost sm" type="button"
                disabled={busy === 'Start all FC'}
                onClick={() => run({
                  label: 'Start all FC',
                  confirm: 'Start the paid bot for every FanCentro model?',
                  request: mut.post('/api/admin/paid-posting/start-all',
                                    { platform: 'fancentro' }),
                  onDone: configs.refetch,
                })}>
          Start All FC
        </button>
        <button className="btn ghost sm" type="button"
                disabled={busy === 'Start all F2F'}
                onClick={() => run({
                  label: 'Start all F2F',
                  confirm: 'Start the paid bot for every F2F model?',
                  request: mut.post('/api/admin/paid-posting/start-all',
                                    { platform: 'f2f' }),
                  onDone: configs.refetch,
                })}>
          Start All F2F
        </button>
        <button className="btn ghost sm" type="button"
                disabled={busy === 'Schedule all'}
                onClick={() => run({
                  label: 'Schedule all',
                  confirm:
                    'Schedule the whole queue? FanCentro needs at least 24 hours '
                    + 'of lead time, so anything sooner is pushed forward.',
                  request: mut.post('/api/admin/paid-posting/schedule-all'),
                  onDone: configs.refetch,
                })}>
          Schedule All
        </button>
        <button className="btn primary sm" type="button"
                onClick={() => setManageFor(rows[0] ?? null)}>
          Create Manual Paid Post
        </button>
      </div>

      {/* 15-10 (fase 4A): de hoofdschakelaar. Zonder hem gebeurt er niets,
          ook niet bij modellen die lopen. Alleen een owner met een gewone login
          kan hem omzetten; de server weigert de rest. */}
      <div className="note" style={{ marginBottom: 12, display: 'flex', gap: 10,
                                     alignItems: 'center', flexWrap: 'wrap' }}>
        <b>Publishing:</b>
        {schakelaar.data?.aan
          ? <Tag kind="bad">ON — scheduled paid FC posts go out</Tag>
          : <Tag kind="mut">OFF — nothing is published</Tag>}
        {schakelaar.data?.door ? (
          <span className="mut2" style={{ fontSize: 12 }}>
            last switched by {schakelaar.data.door}
            {schakelaar.data.op ? ` · ${dt(schakelaar.data.op)}` : ''}
          </span>
        ) : null}
        <span className="mut2" style={{ fontSize: 12 }}>F2F paid is not built yet and is skipped.</span>
        <div className="spacer" style={{ flex: 1 }} />
        <button className="btn ghost sm" type="button" onClick={draaiDroog}>Dry run</button>
        <button className={schakelaar.data?.aan ? 'btn sm' : 'btn ghost sm'} type="button"
                disabled={busy === 'Publishing switch' || schakelaar.loading}
                onClick={() => run({
                  label: 'Publishing switch',
                  confirm: schakelaar.data?.aan
                    ? 'Turn paid publishing OFF? Nothing will be published until it is switched on again.'
                    : 'Turn paid publishing ON? Scheduled paid FanCentro posts of running models '
                      + 'will be published at their planned times, with real prices.',
                  request: mut.put('/api/admin/paid-posting/master-switch',
                                   { aan: !schakelaar.data?.aan }),
                  onDone: schakelaar.refetch,
                })}>
          {schakelaar.data?.aan ? 'Turn OFF' : 'Turn ON'}
        </button>
      </div>

      {droog ? (
        <Modal title="Dry run — what the paid bot would do" width={960}
               desc={droog.note} onClose={() => setDroog(null)}
               footer={<button className="btn ghost sm" type="button"
                               onClick={() => setDroog(null)}>Close</button>}>
          <div style={{ fontSize: 12, marginBottom: 8 }}>
            Publishing is <b>{droog.hoofdschakelaar.aan ? 'ON' : 'OFF'}</b>
            {droog.noodrem ? ' · emergency brake ON' : ''} · window {droog.horizon_days} days
          </div>
          {droog.models.length === 0 ? (
            <div className="mut2" style={{ fontSize: 12 }}>
              No planned paid posts in the window.
            </div>
          ) : droog.models.map((m) => (
            <div key={m.model_persona_id} style={{ marginBottom: 10 }}>
              <b>{m.model}</b> {m.running ? <Tag kind="ok">started</Tag> : <Tag kind="mut">not started</Tag>}
              {m.posts.map((p) => (
                <div key={p.post_id} className="mono" style={{ fontSize: 12 }}>
                  {dt(p.scheduled_at)} · {p.platform} · {p.file} · {p.would}
                </div>
              ))}
            </div>
          ))}
          {droog.f2f_delete_before_post.length ? (
            <>
              <b style={{ fontSize: 13 }}>F2F delete-before-post (shown only, not done)</b>
              {droog.f2f_delete_before_post.map((d) => (
                <div key={`${d.model_persona_id}-${d.day}`} className="mono" style={{ fontSize: 12 }}>
                  model {d.model_persona_id} · {d.day} · {d.paid_posts.length} paid →
                  would delete {d.would_delete.length} free scheduled post(s)
                  {d.short_by ? ` (short by ${d.short_by})` : ''}
                </div>
              ))}
            </>
          ) : null}
        </Modal>
      ) : null}

      {/* Betaalde posts blijven staan. Dat is de reden dat deze bot een eigen
          pagina heeft, en het hoort bovenaan te staan waar niemand eroverheen
          leest. */}
      <div className="note" style={{ marginBottom: 12 }}>
        <b>Paid posts are permanent.</b> They have no expiry, are never touched by
        the vault cleanup and are never retracted automatically. FanCentro also
        locks a post for editing after its first sale. Everything is checked
        before publishing; anything in doubt is parked as <i>needs review</i>
        instead of going out.
      </div>

      {tab === 'planner' ? (
        <Panel title="Week planner"
               desc="Drag a post to a day and time. Within 24 hours is not possible: FanCentro needs more preparation."
               flush>
          <div style={{ padding: 16 }}>
            {planning.loading ? <Loading /> : planning.error ? (
              <ErrorNote error={planning.error} />
            ) : (
              <Weekplanner posts={planning.data?.items ?? []}
                           onGewijzigd={() => { planning.refetch(); configs.refetch() }} />
            )}
          </div>
        </Panel>
      ) : (
      <Panel
        title="Paid Posting Bot"
        desc={`${num(actief)} of ${num(rows.length)} models running`}
        flush
      >
        {configs.loading ? <Loading /> : configs.error ? (
          <ErrorNote error={configs.error} />
        ) : (
          <Table cols={[
            { key: 'm', label: 'Model' },
            { key: 'p', label: 'Platform' },
            { key: 'fc', label: 'FanCentro' },
            { key: 'f2f', label: 'F2F' },
            { key: 'src', label: 'Paid source' },
            { key: 'iv', label: 'Per day' },
            { key: 'q', label: 'Queue', num: true },
            { key: 's', label: 'Status' },
            { key: 'a', label: 'Actions' },
          ]} emptyText="No paid posting configured yet.">
            {rows.map((c) => (
              <tr key={c.id}>
                <td><b>{c.model_name}</b></td>
                <td><Plat value={c.platform} /></td>

                {/* FanCentro: welke van de twee vormen, en de prijs als die
                    er hoort te zijn. */}
                <td>
                  {c.fc_mode === 'unlock_price' ? (
                    <>
                      <Tag kind="acc">Unlockable</Tag>{' '}
                      <span className="mono">{credits(c.fc_unlock_credits)}</span>
                    </>
                  ) : c.fc_mode === 'subscribers_only' ? (
                    <Tag kind="mut">Subscribers only</Tag>
                  ) : <span className="dim">—</span>}
                </td>

                {/* F2F: het omzettype met de bijbehorende prijzen. Bij "Paid
                    for everyone" zijn dat er twee. */}
                <td>
                  {c.f2f_revenue_type ? (
                    <>
                      <Tag kind="acc">
                        {F2F_TYPES.find((t) => t.value === c.f2f_revenue_type)?.label
                          ?? c.f2f_revenue_type}
                      </Tag>{' '}
                      <span className="mono">
                        {c.f2f_revenue_type === 'paid_everyone'
                          ? `${eur(c.f2f_price_followers_eur)} / ${eur(c.f2f_price_fans_eur)}`
                          : eur(c.f2f_price_fans_eur ?? c.f2f_price_followers_eur)}
                      </span>
                    </>
                  ) : <span className="dim">—</span>}
                </td>

                <td className="mut2 mono" style={{ fontSize: 12 }}>
                  {c.paid_source ? c.paid_source : c.nas_suggestion ? (
                    // Eén letter verschil is geen bewijs. Het scherm zegt wat
                    // het vermoedt en laat Jordy beslissen; automatisch
                    // koppelen zou de content van twee modellen door elkaar
                    // kunnen halen, en daar hangt een uitbetaling aan.
                    <span className="onzeker">
                      folder not certain: <b>{c.nas_suggestion}</b> — confirm
                      <button className="btn ghost sm" type="button"
                              style={{ marginLeft: 6 }}
                              onClick={() => koppel(c, c.nas_suggestion as string)}>
                        Link
                      </button>
                      <button className="btn ghost sm" type="button"
                              style={{ marginLeft: 4 }}
                              onClick={() => setKiesMap(c)}>
                        Other folder
                      </button>
                    </span>
                  ) : <span className="dim">not set</span>}
                </td>
                <td className="mut mono">
                  {c.min_per_dag ?? SCHEMA_STANDAARD.min}–{c.max_per_dag ?? SCHEMA_STANDAARD.max}
                </td>
                <td className="num mono">
                  {num(c.queued_count)}
                  {c.needs_review_count ? (
                    <>{' '}<Tag kind="warn">{num(c.needs_review_count)} review</Tag></>
                  ) : null}
                </td>
                <td>
                  {c.plan_status === 'running' ? (
                    <>
                      <Tag kind="ok">Planned</Tag>
                      {c.gepland_tot ? (
                        <span className="mut2" style={{ fontSize: 12, marginLeft: 6 }}>
                          until {dt(c.gepland_tot)}
                        </span>
                      ) : null}
                    </>
                  ) : c.plan_status === 'done'
                    ? <Tag kind="mut">Done</Tag>
                    : <Tag kind="mut">Stopped</Tag>}
                  {/* De daglimiet van F2F hoort zichtbaar te zijn vóórdat de
                      bot ertegenaan loopt. */}
                  {String(c.platform).toLowerCase() === 'f2f'
                    && c.f2f_daily_remaining != null
                    && c.f2f_daily_remaining >= 0 ? (
                    <>{' '}
                      <Tag kind={c.f2f_daily_remaining > 0 ? 'mut' : 'bad'}>
                        {c.f2f_daily_remaining > 0
                          ? `${c.f2f_daily_remaining} left today`
                          : 'daily limit reached'}
                      </Tag>
                    </>
                  ) : null}
                </td>
                <td>
                  <div className="row" style={{ gap: 6 }}>
                    <button className="btn ghost sm" type="button"
                            disabled={busy === 'Toggle paid bot'}
                            onClick={() => run({
                              label: 'Toggle paid bot',
                              confirm: c.plan_status === 'running'
                                ? `Stop the paid bot for ${c.model_name}?`
                                : `Plan the unplanned drafts of ${c.model_name} over the coming `
                                  + `days (${c.min_per_dag ?? SCHEMA_STANDAARD.min}–`
                                  + `${c.max_per_dag ?? SCHEMA_STANDAARD.max} per day)? `
                                  + 'Nothing is published.',
                              request: mut.post(
                                `/api/admin/paid-posting/configs/${c.id}/`
                                + (c.plan_status === 'running' ? 'stop' : 'start')),
                              onDone: () => { configs.refetch(); planning.refetch() },
                            })}>
                      {c.plan_status === 'running' ? 'Stop' : 'Start'}
                    </button>
                    <button className="btn ghost sm" type="button"
                            onClick={() => setManageFor(c)}>
                      Manage
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      )}

      {kiesMap ? (
        <Modal
          title={`Paid folder for ${kiesMap.model_name}`}
          desc="Choose the folder on the NAS that holds this model’s paid content."
          width={460}
          onClose={() => setKiesMap(null)}
          footer={
            <button className="btn ghost sm" type="button"
                    onClick={() => setKiesMap(null)}>Cancel</button>
          }
        >
          <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {(kiesMap.nas_options ?? []).map((m) => (
              <button key={m} type="button"
                      className={m === kiesMap.nas_suggestion ? 'btn sm' : 'btn ghost sm'}
                      onClick={() => koppel(kiesMap, m)}>
                {m}
              </button>
            ))}
          </div>
          <div className="note" style={{ marginTop: 12 }}>
            This only records which folder belongs to this model. Nothing is
            moved, copied or deleted.
          </div>
        </Modal>
      ) : null}

      {manageFor ? (
        <ManageDialoog config={manageFor} onClose={() => setManageFor(null)}
                       onGewijzigd={configs.refetch} />
      ) : null}
    </>
  )
}

/* ------------------------------------------------------------------ manage */

interface PaidItem {
  id: number
  file: string
  preview_file: string
  title: string
  tags: string
  description: string
  fc_mode: string
  fc_unlock_credits: number | null
  f2f_revenue_type: string
  f2f_price_followers_eur: number | null
  f2f_price_fans_eur: number | null
  publish_at: string
  /** Waar de miniatuur vandaan komt; leeg als er geen te maken was. */
  thumbnail_url: string | null
  preview_thumbnail_url: string | null
  kind: 'photo' | 'video' | 'text'
  /** fc_post, f2f_post of fc_clip. */
  post_kind: string
  /** Alleen bij een clip: waar hij staat in de beoordeling. */
  review_status: string | null
  /** Lengte van de video in seconden, als die te bepalen was. */
  duration_seconds: number | null
  status: string
  problems: string[] | null
  /** 10-10 (Jordy): voor het overzicht per model. */
  platform: string
  price_label: string
  /** Livetijd in Amsterdamse tijd (DD-MM-YYYY HH:MM); leeg als niet gepland. */
  live_at_ams: string
  /** not planned | planned | publishing | handed over | live | failed | ... */
  state: string
}

/** Eén CSV-regel zoals het voorbeeld hem toont. */
type ImportRegel = {
  file: string; kind: string; title: string; publish_at: string
  fc_mode: string; fc_unlock_credits: number | null; f2f_revenue_type: string
  f2f_price_followers_eur: number | null; f2f_price_fans_eur: number | null
  [veld: string]: unknown
}

/** Wat /sync-preview teruggeeft: wat een import zou doen, zonder iets te doen. */
type ImportVoorbeeld = {
  available: boolean
  detail?: string
  path?: string
  new: ImportRegel[]
  changed: { from: ImportRegel; to: ImportRegel }[]
  locked: { from: ImportRegel; to: ImportRegel }[]
  removed: string[]
  invalid: { file: string; problems: string[] }[]
  media_without_row: string[]
  row_without_media: string[]
  preview_key: string
  /** Fase 3: versie van paid.csv; opslaan weigert als hij sindsdien veranderde. */
  csv_version: string
  csv_open_in_excel: boolean
  /** Alleen in het dashboard gewijzigd (of nog nooit in de CSV): gaat naar de CSV. */
  to_csv: ImportRegel[]
  /** Aan beide kanten gewijzigd: "from" = dashboard, "to" = CSV. Jordy kiest. */
  conflicts: { from: ImportRegel; to: ImportRegel }[]
}

/** De prijs van een CSV-regel in de munt van het platform. */
function regelPrijs(r: ImportRegel): string {
  if (r.kind !== 'f2f_post') {
    if (r.fc_mode === 'subscribers_only') return 'subscribers only'
    return r.fc_unlock_credits != null ? credits(r.fc_unlock_credits) : '—'
  }
  if (r.f2f_revenue_type === 'paid_everyone') {
    return `${eur(r.f2f_price_followers_eur)} / ${eur(r.f2f_price_fans_eur)}`
  }
  return eur(r.f2f_price_fans_eur ?? r.f2f_price_followers_eur)
}

/** Welke velden er tussen dashboard en CSV verschillen. */
function verschilVelden(a: ImportRegel, b: ImportRegel): string {
  return Object.keys(b).filter((k) => String(a[k] ?? '') !== String(b[k] ?? ''))
    .join(', ')
}

/** Een miniatuur uit de paid-map; een <img> stuurt geen header, dus het token gaat mee. */
function bestandMiniatuur(modelId: number, file: string): string {
  return `/api/admin/paid-posting/file-thumbnail/${modelId}?file=${encodeURIComponent(file)}`
    + `&token=${encodeURIComponent(mediaToken())}`
}

function metToken(url: string | null): string | undefined {
  if (!url) return undefined
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(mediaToken())}`
}

/**
 * Fase 3 (Jordy 10-10): titel, prijs en golive-tijd voor één bestand. Opslaan
 * schrijft de regel in paid.csv én maakt (of werkt bij) de concept-post. De
 * server controleert alles opnieuw en weigert als de CSV sinds het laden
 * veranderde of in Excel openstaat.
 */
function BestandFormulier({ config, file, csvVersion, begin, onSaved, onCancel }: {
  config: PaidConfig
  file: string
  csvVersion: string
  begin?: PaidItem
  onSaved: () => void
  onCancel?: () => void
}) {
  const { run, busy } = useActions()
  const fc = config.platform === 'fancentro'
  const tekst = (v: unknown) => (v == null ? '' : String(v))
  const [titel, setTitel] = useState(begin?.title ?? '')
  const [tags, setTags] = useState(begin?.tags ?? '')
  const [golive, setGolive] = useState(begin?.live_at_ams ?? '')
  const [fcModus, setFcModus] = useState(begin?.fc_mode || config.fc_mode || 'unlock_price')
  const [fcPrijs, setFcPrijs] = useState(tekst(begin ? begin.fc_unlock_credits : config.fc_unlock_credits))
  const [f2fType, setF2fType] = useState(
    begin?.f2f_revenue_type || config.f2f_revenue_type || 'exclusive_fans')
  const [volgers, setVolgers] = useState(
    tekst(begin ? begin.f2f_price_followers_eur : config.f2f_price_followers_eur))
  const [fans, setFans] = useState(
    tekst(begin ? begin.f2f_price_fans_eur : config.f2f_price_fans_eur))

  /** Leeg = geen prijs; iets wat geen getal is wordt een fout, niet stil "geen prijs". */
  function getal(t: string, naam: string): number | null {
    if (t.trim() === '') return null
    const n = Number(t.trim().replace(',', '.'))
    if (!Number.isFinite(n)) throw new Error(`${naam} is not a number`)
    return n
  }

  const opslaan = () => run({
    label: `Save ${file}`,
    request: async () => {
      await api.post('/api/admin/paid-posting/files/save', {
        model_persona_id: config.model_persona_id,
        csv_version: csvVersion,
        items: [{
          file, title: titel, tags, publish_at: golive.trim(),
          fc_mode: fc ? fcModus : '',
          fc_unlock_credits: fc && fcModus === 'unlock_price' ? getal(fcPrijs, 'Price in credits') : null,
          f2f_revenue_type: fc ? '' : f2fType,
          f2f_price_followers_eur: fc ? null : getal(volgers, 'Followers price'),
          f2f_price_fans_eur: fc ? null : getal(fans, 'Fans price'),
        }],
      })
    },
    onDone: onSaved,
  })

  return (
    <div className="paidkaart">
      <div className="beeld">
        <img src={bestandMiniatuur(config.model_persona_id, file)} alt={file} loading="lazy" />
      </div>
      <div className="tekst" style={{ display: 'grid', gap: 5 }}>
        <span className="mut2" style={{ fontSize: 11 }}>{file}</span>
        <input className="inp" placeholder="Title" value={titel}
               onChange={(e) => setTitel(e.target.value)} />
        <input className="inp" placeholder="Tags (at least two, e.g. #tag1 #tag2)" value={tags}
               onChange={(e) => setTags(e.target.value)} />
        <input className="inp" placeholder="Go-live DD-MM-YYYY HH:MM (Amsterdam)" value={golive}
               onChange={(e) => setGolive(e.target.value)} />
        {fc ? (
          <div className="row" style={{ gap: 6 }}>
            <select className="select" value={fcModus} onChange={(e) => setFcModus(e.target.value)}>
              {FC_MODI.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            {fcModus === 'unlock_price' ? (
              <input className="inp" style={{ width: 90 }} placeholder="credits" value={fcPrijs}
                     onChange={(e) => setFcPrijs(e.target.value)} />
            ) : null}
          </div>
        ) : (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <select className="select" value={f2fType} onChange={(e) => setF2fType(e.target.value)}>
              {F2F_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input className="inp" style={{ width: 90 }} placeholder="€ followers" value={volgers}
                   onChange={(e) => setVolgers(e.target.value)} />
            <input className="inp" style={{ width: 90 }} placeholder="€ fans" value={fans}
                   onChange={(e) => setFans(e.target.value)} />
          </div>
        )}
        <div className="row" style={{ gap: 6 }}>
          <button className="btn sm" type="button" disabled={!!busy || !titel.trim()}
                  onClick={opslaan}>Save</button>
          {onCancel ? (
            <button className="btn ghost sm" type="button" onClick={onCancel}>Cancel</button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/**
 * De inhoud van de betaalde map van één model, met miniaturen en alles ter
 * plekke aanpasbaar.
 *
 * Zonder miniatuur is dit een lijst bestandsnamen, en dan is niet te zien wat
 * er straks te koop staat — precies de vergissing die je bij betaalde content
 * niet wilt maken.
 */
/** Min en max posts per dag (fase 2). Start plant de concepten volgens een
 *  wisselende reeks binnen deze grenzen; er wordt niets gepubliceerd. */
function DagSchema({ config, onGewijzigd }: {
  config: PaidConfig
  onGewijzigd?: () => void
}) {
  const { run, busy } = useActions()
  const [mn, setMn] = useState(String(config.min_per_dag ?? SCHEMA_STANDAARD.min))
  const [mx, setMx] = useState(String(config.max_per_dag ?? SCHEMA_STANDAARD.max))
  const opslaan = () => run({
    label: 'Posts per day',
    request: mut.put('/api/admin/paid-posting/defaults', {
      model_persona_id: config.model_persona_id,
      min_per_dag: Number(mn), max_per_dag: Number(mx),
    }),
    onDone: onGewijzigd,
    successText: `${config.model_name}: ${mn}–${mx} posts per day saved.`,
  })
  return (
    <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 10,
                                  flexWrap: 'wrap', fontSize: 12 }}>
      <span className="mut2">Posts per day</span>
      <input className="inp" type="number" min={1} max={10} value={mn}
             style={{ width: 60 }} aria-label="Minimum posts per day"
             onChange={(e) => setMn(e.target.value)} />
      <span className="mut2">to</span>
      <input className="inp" type="number" min={1} max={10} value={mx}
             style={{ width: 60 }} aria-label="Maximum posts per day"
             onChange={(e) => setMx(e.target.value)} />
      <button className="btn ghost sm" type="button" disabled={!!busy} onClick={opslaan}>
        Save
      </button>
      <span className="mut2">
        Start spreads the drafts over the coming days with a varying number per day
        within this range. Nothing is published.
      </span>
    </div>
  )
}

function ManageDialoog({ config, onClose, onGewijzigd }: {
  config: PaidConfig
  onClose: () => void
  onGewijzigd?: () => void
}) {
  const { run, busy, notify } = useActions()
  const [filter, setFilter] = useState('all')
  // 10-10 (Jordy): standaard het overzicht als tabel; de kaarten blijven.
  const [weergave, setWeergave] = useState('table')
  const [voorbeeld, setVoorbeeld] = useState<ImportVoorbeeld | null>(null)
  // Fase 3: de stand van map en CSV, bij openen geladen (alleen lezen).
  const [stand, setStand] = useState<ImportVoorbeeld | null>(null)
  const [keuzes, setKeuzes] = useState<Record<string, string>>({})
  const [bewerk, setBewerk] = useState<PaidItem | null>(null)
  const items = useApi<{ items: PaidItem[] }>(
    `/api/admin/paid-posting/items?model_persona_id=${config.model_persona_id}`,
    [config.model_persona_id])

  const alle = items.data?.items ?? []
  const tellers = useMemo(() => {
    const t = { text: 0, photo: 0, video: 0 }
    for (const i of alle) {
      if (i.kind === 'video') t.video += 1
      else if (i.kind === 'photo') t.photo += 1
      if (i.title) t.text += 1
    }
    return t
  }, [alle])

  const zichtbaar = useMemo(() => alle.filter((i) => (
    filter === 'all' ? true
    : filter === 'queued' ? i.status === 'queued'
    : filter === 'posted' ? i.status === 'posted'
    : i.status === 'needs_review'
  )), [alle, filter])

  // Oud waarschuwt zodra er meer of minder teksten zijn dan media: dan
  // eindigt er content zonder onderschrift, of blijft er tekst over.
  const scheef = tellers.text !== tellers.photo + tellers.video

  /**
   * Alleen titel, beschrijving en tags van de betaalde posts vervangen.
   *
   * Het bestand gaat naar de server en wordt daar gelezen met dezelfde lezer
   * als Sync from NAS. Prijzen, PPV en de verkoopvorm worden nergens geraakt;
   * dat is hier zwaarder dan bij de gratis bot, want een prijs die verschuift
   * is geld.
   */
  async function vervangBetaaldeTeksten(bestand: File) {
    const pad = `/api/admin/paid-posting/replace-texts`
      + `?model_persona_id=${config.model_persona_id}`

    async function stuur(dryRun: boolean) {
      const fd = new FormData()
      fd.append('file', bestand)
      return api.postForm<PaidVervanging>(`${pad}&dry_run=${dryRun}`, fd)
    }

    let proef: PaidVervanging | null = null
    try {
      proef = await stuur(true)
    } catch (e) {
      notify(String((e as Error)?.message || e), false)
      return
    }
    if (!proef || !proef.gekoppeld) {
      notify(proef ? 'No line in the file matches a paid post here'
                   : 'Could not read the preview', false)
      return
    }
    await run({
      label: 'Replace texts',
      confirm: `Replace the texts of ${proef.gekoppeld} paid posts?\n\n`
        + `  ${proef.csv_zonder_post} lines have no matching post here\n`
        + `  ${proef.posts_zonder_csv} paid posts are not in the file and stay`
        + ' as they are\n'
        + `  ${proef.al_op_platform} are already on the platform — their text`
        + ' there does NOT change\n'
        + `  ${proef.nu_aan_het_publiceren} are being published right now and`
        + ' are skipped\n'
        + `  ${proef.oude_tags_behouden} keep their existing tags (too few`
        + ' usable ones in the file)\n\n'
        + 'Prices, PPV and the sale type are NOT touched, even though the file'
        + ' contains those columns. The old texts are backed up first.',
      request: async () => {
        const uit = await stuur(false)
        notify(`Replaced ${uit?.changed ?? 0} texts. `
               + `Old texts saved to ${uit?.backup ?? '?'}`, true)
      },
      onDone: items.refetch,
    })
  }

  /** De stand van map en CSV lezen; verandert niets. */
  async function laadStand(): Promise<ImportVoorbeeld | null> {
    try {
      const v = await api.post<ImportVoorbeeld>('/api/admin/paid-posting/sync-preview',
        { model_persona_id: config.model_persona_id })
      setStand(v?.available ? v : null)
      return v
    } catch (e) {
      notify(String((e as Error)?.message || e), false)
      return null
    }
  }

  useEffect(() => { laadStand() }, [config.model_persona_id])

  function naOpslaan() {
    setBewerk(null)
    items.refetch()
    laadStand()
  }

  /** Eerst het voorbeeld tonen; overnemen kan alleen vanuit dat voorbeeld. */
  async function toonVoorbeeld() {
    const v = await laadStand()
    if (!v) return
    if (!v.available) {
      notify(v.detail ?? 'No paid folder found on the NAS', false)
      return
    }
    setKeuzes({})
    setVoorbeeld(v)
  }

  function neemOver(v: ImportVoorbeeld) {
    run({
      label: 'Apply import',
      confirm: `Apply this sync?\n\n`
        + `  ${v.new.length} new paid posts (added as drafts, nothing is published)\n`
        + `  ${v.changed.length} posts updated from the CSV (old values are backed up first)\n`
        + `  ${v.to_csv.length} rows written to paid.csv (the CSV is backed up first)\n`
        + `  ${Object.keys(keuzes).length} of ${v.conflicts.length} conflicts resolved by your choice\n\n`
        + `Not touched: ${v.locked.length} already on the platform, `
        + `${v.removed.length} no longer in the CSV, ${v.invalid.length} invalid rows.`,
      request: async () => {
        const uit = await api.post<{ added: number; updated: number; written_to_csv: number }>(
          '/api/admin/paid-posting/sync-apply',
          { model_persona_id: config.model_persona_id, preview_key: v.preview_key,
            choices: keuzes })
        notify(`Synced: ${uit?.added ?? 0} added, ${uit?.updated ?? 0} updated, `
               + `${uit?.written_to_csv ?? 0} written to the CSV`, true)
        setVoorbeeld(null)
      },
      onDone: naOpslaan,
    })
  }

  return (
    <Modal
      title={`Paid content — ${config.model_name}`}
      desc={config.paid_source
        ? `From ${config.paid_source}`
        : 'No paid source configured for this model yet.'}
      width={1080}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Close</button>
          {/* 10-09 (Jordy): dezelfde mogelijkheid als bij de gratis bot —
              alleen de teksten vervangen. Het formaat is hier anders (deze CSV
              koppelt op bestandsnaam en draagt de prijzen), dus hij gaat door
              de lezer van dit scherm en niet die van de wachtrij. De
              prijskolommen mogen erin staan en worden genegeerd. */}
          <label className={`btn ghost sm${busy ? ' disabled' : ''}`}
                 style={{ cursor: 'pointer' }}>
            Replace texts (CSV)
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null
                e.target.value = ''
                if (f) vervangBetaaldeTeksten(f)
              }}
            />
          </label>
          <button className="btn ghost sm" type="button"
                  disabled={!!busy}
                  onClick={toonVoorbeeld}>
            Sync from NAS
          </button>
        </>
      }
    >
      <DagSchema config={config} onGewijzigd={onGewijzigd} />
      <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 10,
                                    flexWrap: 'wrap' }}>
        <span className="mut2" style={{ fontSize: 12 }}>
          {num(tellers.text)} texts · {num(tellers.photo)} photos ·{' '}
          {num(tellers.video)} videos
        </span>
        {scheef ? (
          <Tag kind="warn">
            texts and media do not match — some posts would go out without a caption
          </Tag>
        ) : null}
        <div className="spacer" style={{ flex: 1 }} />
        <Seg
          options={[
            { value: 'table', label: 'Table' },
            { value: 'cards', label: 'Cards' },
          ]}
          value={weergave}
          onChange={setWeergave}
        />
        <Seg
          options={[
            { value: 'all', label: 'All' },
            { value: 'queued', label: 'To post' },
            { value: 'posted', label: 'Posted' },
            { value: 'needs_review', label: 'Needs review' },
          ]}
          value={filter}
          onChange={setFilter}
        />
      </div>

      {voorbeeld ? (
        <div className="panel" style={{ padding: 12, marginBottom: 12 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <b>Import preview</b>
            <span className="mut2" style={{ fontSize: 12 }}>{voorbeeld.path}</span>
            <div className="spacer" style={{ flex: 1 }} />
            <button className="btn ghost sm" type="button"
                    onClick={() => setVoorbeeld(null)}>Close preview</button>
            <button className="btn sm" type="button"
                    disabled={!!busy || (voorbeeld.new.length + voorbeeld.changed.length
                      + voorbeeld.to_csv.length + Object.keys(keuzes).length) === 0}
                    onClick={() => neemOver(voorbeeld)}>
              Apply sync
            </button>
          </div>
          <div className="mut2" style={{ fontSize: 12, marginBottom: 6 }}>
            {voorbeeld.new.length} new · {voorbeeld.changed.length} changed in the CSV ·{' '}
            {voorbeeld.to_csv.length} to write to the CSV · {voorbeeld.conflicts.length} conflicts ·{' '}
            {voorbeeld.locked.length} locked (already on the platform) ·{' '}
            {voorbeeld.removed.length} no longer in the CSV (kept) ·{' '}
            {voorbeeld.invalid.length} invalid · {voorbeeld.row_without_media.length} rows
            without a file · {voorbeeld.media_without_row.length} files without a row
          </div>
          {voorbeeld.new.length + voorbeeld.changed.length > 0 ? (
            <Table cols={[
              { key: 'w', label: 'What' }, { key: 'f', label: 'File' },
              { key: 't', label: 'Title' }, { key: 'l', label: 'Live (Amsterdam)' },
              { key: 'p', label: 'Price' }, { key: 'd', label: 'Changed fields' },
            ]}>
              {voorbeeld.new.map((r) => (
                <tr key={`n-${r.file}`}>
                  <td><Tag kind="ok">new</Tag></td><td>{r.file}</td><td>{r.title}</td>
                  <td>{r.publish_at || 'not planned'}</td><td>{regelPrijs(r)}</td><td />
                </tr>
              ))}
              {voorbeeld.changed.map((c) => (
                <tr key={`c-${c.to.file}`}>
                  <td><Tag kind="warn">changed</Tag></td><td>{c.to.file}</td>
                  <td>{c.to.title}</td><td>{c.to.publish_at || 'not planned'}</td>
                  <td>{regelPrijs(c.to)}</td><td>{verschilVelden(c.from, c.to)}</td>
                </tr>
              ))}
            </Table>
          ) : (
            <div className="mut2" style={{ fontSize: 12 }}>Nothing to import.</div>
          )}
          {voorbeeld.csv_open_in_excel ? (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <Tag kind="bad">open in Excel</Tag> paid.csv is open; close it before applying.
            </div>
          ) : null}
          {voorbeeld.to_csv.length ? (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <Tag kind="acc">to CSV</Tag> changed in the dashboard, written to paid.csv:{' '}
              {voorbeeld.to_csv.map((r) => r.file).join(', ')}
            </div>
          ) : null}
          {voorbeeld.conflicts.length ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>
                <Tag kind="warn">conflict</Tag> changed on both sides; nothing is overwritten
                until you choose:
              </div>
              <Table cols={[
                { key: 'f', label: 'File' }, { key: 'd', label: 'Dashboard' },
                { key: 'c', label: 'CSV' }, { key: 'k', label: 'Keep' },
              ]}>
                {voorbeeld.conflicts.map((c) => (
                  <tr key={`k-${c.to.file}`}>
                    <td>{c.to.file}<div className="mut2" style={{ fontSize: 11 }}>
                      {verschilVelden(c.from, c.to)}</div></td>
                    <td>{c.from.title} · {c.from.publish_at || 'not planned'} · {regelPrijs(c.from)}</td>
                    <td>{c.to.title} · {c.to.publish_at || 'not planned'} · {regelPrijs(c.to)}</td>
                    <td>
                      <Seg
                        options={[
                          { value: '', label: 'Later' },
                          { value: 'dashboard', label: 'Dashboard' },
                          { value: 'csv', label: 'CSV' },
                        ]}
                        value={keuzes[c.to.file] ?? ''}
                        onChange={(k) => setKeuzes((oud) => {
                          const nieuw = { ...oud }
                          if (k) nieuw[c.to.file] = k
                          else delete nieuw[c.to.file]
                          return nieuw
                        })}
                      />
                    </td>
                  </tr>
                ))}
              </Table>
            </div>
          ) : null}
          {voorbeeld.invalid.length ? (
            <div style={{ marginTop: 8 }}>
              {voorbeeld.invalid.map((r) => (
                <div key={`i-${r.file}`} style={{ fontSize: 12 }}>
                  <Tag kind="bad">invalid</Tag> {r.file}: {r.problems.join('; ')}
                </div>
              ))}
            </div>
          ) : null}
          {voorbeeld.locked.length ? (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <Tag kind="mut">locked</Tag> changed in the CSV but already on the platform,
              not imported: {voorbeeld.locked.map((c) => c.to.file).join(', ')}
            </div>
          ) : null}
          {voorbeeld.removed.length ? (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <Tag kind="mut">kept</Tag> no longer in the CSV, nothing is deleted:{' '}
              {voorbeeld.removed.join(', ')}
            </div>
          ) : null}
          {voorbeeld.row_without_media.length ? (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <Tag kind="bad">no file</Tag> {voorbeeld.row_without_media.join(', ')}
            </div>
          ) : null}
        </div>
      ) : null}

      {bewerk && stand ? (
        <div style={{ marginBottom: 12 }}>
          <b style={{ fontSize: 13 }}>Edit</b>
          <div className="paidgrid" style={{ marginTop: 6 }}>
            <BestandFormulier key={`b-${bewerk.id}`} config={config} file={bewerk.file}
                              csvVersion={stand.csv_version} begin={bewerk}
                              onSaved={naOpslaan} onCancel={() => setBewerk(null)} />
          </div>
        </div>
      ) : null}

      {stand && stand.media_without_row.length ? (
        <div style={{ marginBottom: 12 }}>
          <b style={{ fontSize: 13 }}>
            Files without a row ({stand.media_without_row.length})
          </b>
          <span className="mut2" style={{ fontSize: 12, marginLeft: 8 }}>
            Saving writes the row to paid.csv and adds a draft post; nothing is published.
          </span>
          {stand.csv_open_in_excel ? (
            <div style={{ fontSize: 12, marginTop: 4 }}>
              <Tag kind="bad">open in Excel</Tag> close paid.csv before saving.
            </div>
          ) : null}
          <div className="paidgrid" style={{ marginTop: 6 }}>
            {stand.media_without_row.map((f) => (
              <BestandFormulier key={f} config={config} file={f}
                                csvVersion={stand.csv_version} onSaved={naOpslaan} />
            ))}
          </div>
        </div>
      ) : null}

      {items.loading ? <Loading /> : zichtbaar.length === 0 ? (
        <div className="mut2" style={{ fontSize: 12 }}>Nothing here.</div>
      ) : weergave === 'table' ? (
        <Table cols={[
          { key: 'title', label: 'Title' },
          { key: 'live', label: 'Live (Amsterdam)' },
          { key: 'price', label: 'Price' },
          { key: 'platform', label: 'Platform' },
          { key: 'state', label: 'Status' },
          { key: 'edit', label: '' },
        ]}>
          {zichtbaar.map((i) => (
            <tr key={i.id}>
              <td>
                {i.title || <span className="dim">no title</span>}
                <div className="mut2" style={{ fontSize: 11 }}>{i.file}</div>
              </td>
              <td>{i.live_at_ams || <span className="dim">not planned</span>}</td>
              <td>{i.price_label}</td>
              <td><Plat value={i.platform} /></td>
              <td>
                <Tag kind={i.state === 'live' ? 'ok'
                  : ['failed', 'needs review'].includes(i.state) ? 'bad'
                  : i.state === 'handed over' || i.state === 'publishing' ? 'acc' : 'mut'}>
                  {i.state}
                </Tag>
              </td>
              <td>
                {['not planned', 'planned'].includes(i.state) && stand ? (
                  <button className="btn ghost sm" type="button"
                          onClick={() => setBewerk(i)}>Edit</button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      ) : (
        <div className="paidgrid">
          {zichtbaar.map((i) => (
            <div className="paidkaart" key={i.file}>
              <div className="beeld">
                {i.thumbnail_url ? (
                  <img src={metToken(i.thumbnail_url)} alt={i.file} loading="lazy" />
                ) : (
                  <span className="geen">{i.kind === 'video' ? 'video' : 'no preview'}</span>
                )}
                {/* De teaser staat apart: dat is wat een niet-betalende fan
                    te zien krijgt, en dat moet je los kunnen beoordelen. */}
                {i.preview_thumbnail_url ? (
                  <img className="teaser" src={metToken(i.preview_thumbnail_url)}
                       alt="Free preview" loading="lazy" title="Free preview" />
                ) : null}
              </div>
              <div className="tekst">
                <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <Tag kind={i.post_kind === 'fc_clip' ? 'warn' : 'mut'}>
                    {SOORTEN[i.post_kind] ?? i.post_kind}
                  </Tag>
                  {i.review_status && REVIEW[i.review_status] ? (
                    <Tag kind={REVIEW[i.review_status].kind}>
                      {REVIEW[i.review_status].label}
                    </Tag>
                  ) : null}
                  {i.duration_seconds ? (
                    <span className="mut2" style={{ fontSize: 12 }}>
                      {Math.floor(i.duration_seconds / 60)}:
                      {String(Math.round(i.duration_seconds % 60)).padStart(2, '0')}
                    </span>
                  ) : null}
                </div>
                <b>{i.title || <span className="dim">no title</span>}</b>
                <div className="mut2" style={{ fontSize: 12 }}>{i.tags}</div>
                {i.description ? (
                  <div className="mut" style={{ fontSize: 12 }}>{i.description}</div>
                ) : null}
                <div className="row" style={{ gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
                  {i.fc_mode === 'unlock_price' ? (
                    <Tag kind="acc">FC {credits(i.fc_unlock_credits)}</Tag>
                  ) : i.fc_mode === 'subscribers_only' ? (
                    <Tag kind="mut">FC subscribers only</Tag>
                  ) : null}
                  {i.f2f_revenue_type ? (
                    <Tag kind="acc">
                      F2F {F2F_TYPES.find((t) => t.value === i.f2f_revenue_type)?.label}
                      {' '}
                      {i.f2f_revenue_type === 'paid_everyone'
                        ? `${eur(i.f2f_price_followers_eur)} / ${eur(i.f2f_price_fans_eur)}`
                        : eur(i.f2f_price_fans_eur ?? i.f2f_price_followers_eur)}
                    </Tag>
                  ) : null}
                  {i.publish_at ? (
                    <Tag kind="mut">{dt(i.publish_at)}</Tag>
                  ) : <Tag kind="mut">queued</Tag>}
                </div>
                {i.problems?.length ? (
                  <div className="row" style={{ gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    {i.problems.map((p, n) => <Tag kind="bad" key={n}>{p}</Tag>)}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mut2" style={{ fontSize: 12, marginTop: 10 }}>
        Price in credits (100 credits = $1); minimum {FC_MIN_CREDITS} credits;
        {' '}{FC_MODI.map((m) => m.label).join(' or ')}.
      </div>
    </Modal>
  )
}
